import React, { useState, useContext, useEffect } from 'react'
import AppLayout from '../layouts/AppLayout'
import { AppContext } from '../contexts/AppContext'
import { FaShieldAlt, FaSearch, FaFilter } from 'react-icons/fa'
import Bounce from 'react-activity/dist/Bounce'
import 'react-activity/dist/Bounce.css'

const AllVPNs = () => {
  const { isLoading, getVpns } = useContext(AppContext)

  // State for data
  const [vpns, setVpns] = useState([])
  const [filteredVpns, setFilteredVpns] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set())

  // Load VPNs data
  useEffect(() => {
    const loadVPNs = async () => {
      try {
        const vpnList = await getVpns()
        if (vpnList) {
          setVpns(vpnList)
          setFilteredVpns(vpnList)
        }
      } catch (error) {
        console.error('Error loading VPNs:', error)
      }
    }

    loadVPNs()
  }, [getVpns])

  // Filter VPNs based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredVpns(vpns)
    } else {
      const filtered = vpns.filter(
        (vpn) =>
          vpn.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (vpn.description && vpn.description.toLowerCase().includes(searchTerm.toLowerCase()))
      )
      setFilteredVpns(filtered)
    }
  }, [searchTerm, vpns])

  // Handle search
  const handleSearch = (e) => {
    setSearchTerm(e.target.value)
  }

  // Clear search
  const clearSearch = () => {
    setSearchTerm('')
  }

  // Toggle description expansion
  const toggleDescription = (vpnId) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(vpnId)) {
        newSet.delete(vpnId)
      } else {
        newSet.add(vpnId)
      }
      return newSet
    })
  }

  // Check if description should be truncated
  const shouldTruncate = (description) => {
    return description && description.length > 100
  }

  // Get truncated description
  const getTruncatedDescription = (description) => {
    return description.substring(0, 100) + '...'
  }

  return (
    <AppLayout pageName='Available VPNs'>
      <div className='container mx-auto px-4 py-6'>
        <div className='max-w-6xl mx-auto'>
          {/* Header */}
          <div className='text-center mb-8'>
            <div className='inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4'>
              <FaShieldAlt className='w-8 h-8 text-purple-600' />
            </div>
            <h1 className='text-3xl font-bold text-gray-900 mb-2'>Available VPNs</h1>
            <p className='text-gray-600'>Browse and explore all available VPN services for testing</p>
          </div>

          {/* Search and Filter Section */}
          <div className='bg-white rounded-lg shadow-lg p-6 mb-6'>
            <div className='flex items-center justify-between mb-6'>
              <div className='flex items-center'>
                <FaSearch className='w-5 h-5 text-gray-600 mr-2' />
                <h2 className='text-xl font-semibold text-gray-900'>Search VPNs</h2>
              </div>
              <span className='bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full'>
                {filteredVpns.length} of {vpns.length} VPNs
              </span>
            </div>

            <div className='flex gap-4 mb-4'>
              <div className='flex-1'>
                <div className='relative'>
                  <input
                    type='text'
                    className='w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent'
                    placeholder='Search VPNs by name or description...'
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                  <FaSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400' />
                </div>
              </div>
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className='px-4 py-3 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors'
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* VPN List Section */}
          <div className='bg-white rounded-lg shadow-lg p-6'>
            <div className='flex items-center justify-between mb-6'>
              <div className='flex items-center'>
                <FaShieldAlt className='w-6 h-6 text-purple-600 mr-3' />
                <h2 className='text-2xl font-bold text-gray-900'>VPN Services</h2>
              </div>
              <span className='bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full'>
                {filteredVpns.length} VPNs
              </span>
            </div>

            {isLoading ? (
              <div className='flex justify-center items-center py-12'>
                <Bounce />
                <span className='ml-3 text-gray-600'>Loading VPNs...</span>
              </div>
            ) : filteredVpns.length === 0 ? (
              <div className='text-center py-12'>
                <FaShieldAlt className='w-16 h-16 text-gray-300 mx-auto mb-4' />
                <h3 className='text-lg font-medium text-gray-900 mb-2'>No VPNs Found</h3>
                <p className='text-gray-600'>
                  {searchTerm ? 'Try adjusting your search terms.' : 'No VPN services are currently available.'}
                </p>
              </div>
            ) : (
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                {filteredVpns.map((vpn) => (
                  <div
                    key={vpn.id}
                    className='flex flex-col border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-all duration-200 hover:border-purple-300'
                  >
                    <div className='flex items-start mb-4 flex-1'>
                      {vpn.logo_path && (
                        <img
                          src={`${process.env.REACT_APP_STATIC_URL}${vpn.logo_path}`}
                          alt={vpn.name}
                          className='w-12 h-12 object-contain rounded mr-4'
                          onError={(e) => {
                            e.target.style.display = 'none'
                          }}
                        />
                      )}
                      <div className='flex-1'>
                        <h3 className='text-xl font-semibold text-gray-900 mb-1'>{vpn.name}</h3>
                        {vpn.description && (
                          <div>
                            <p className='text-sm text-gray-600 leading-relaxed'>
                              {expandedDescriptions.has(vpn.id) 
                                ? vpn.description 
                                : shouldTruncate(vpn.description) 
                                  ? getTruncatedDescription(vpn.description)
                                  : vpn.description
                              }
                            </p>
                            {shouldTruncate(vpn.description) && (
                              <button
                                onClick={() => toggleDescription(vpn.id)}
                                className='text-purple-600 hover:text-purple-800 text-sm font-medium mt-1 transition-colors'
                              >
                                {expandedDescriptions.has(vpn.id) ? 'See less' : 'See more'}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Additional VPN details could go here */}
                    <div className='mt-4 pt-4 border-t border-gray-100'>
                      <div className='flex items-center justify-between text-sm text-gray-500'>
                        <span>VPN ID: {vpn.id}</span>
                        <span className='bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium'>
                          Active
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default AllVPNs
